/**
 * TicketForm.tsx
 *
 * Sub-form for adding/editing a single ticket type within the event form.
 * Mirrors Flutter's CreateTicketDetailScreen + EventTicketModel.
 */

import React, { useState } from 'react';
import { FiTag, FiChevronDown, FiCheck, FiPlus, FiTrash2, FiZap, FiClock } from 'react-icons/fi';
import { Button } from '../ui/Button';
import { FormLabel } from '../ui/FormLabel';
import { DateInput } from '../ui/DateInput';
import { Input } from '../ui/Input';
import { getEventDurationHours } from '../../utils/ticketExpiry';

interface TicketFormProps {
  mode: 'add' | 'edit';
  eventStartDate?: string;
  eventStartTime?: string;
  eventEndDate?: string;
  eventEndTime?: string;
  showSubEventToggle?: boolean;
  /** True when editing a ticket whose sales period has already ended — locks the form. */
  isExpired?: boolean;
  initialTicket?: {
    id?: number;
    ticket_type: string;
    price: number;
    quantity: number;
    ticket_number_issued?: number;
    remaining_tickets?: number;
    description?: string;
    sales_start_date?: string;
    sales_start_time?: string;
    sales_end_date?: string;
    sales_end_time?: string;
    has_end_purchase_period?: boolean;
    go_live_on_publish?: boolean;
    sub_events?: boolean;
    multi_day_access?: boolean;
  };
  onSave: (ticket: {
    id?: number;
    ticket_type: string;
    price: number;
    quantity: number;
    description?: string;
    sales_start_date?: string;
    sales_start_time?: string;
    sales_end_date?: string;
    sales_end_time?: string;
    has_end_purchase_period?: boolean;
    go_live_on_publish?: boolean;
    sub_events?: boolean;
    multi_day_access?: boolean;
  }) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

const ToggleRow = ({
  icon,
  label,
  sublabel,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/60 border border-white/8 hover:border-white/15 transition-all text-left"
  >
    <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white text-sm font-medium">{label}</p>
      <p className="text-zinc-500 text-xs">{sublabel}</p>
    </div>
    <div
      className={`w-11 h-6 rounded-full p-0.5 transition-colors ${
        checked ? 'bg-blue-500' : 'bg-zinc-700'
      }`}
    >
      <div
        className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </div>
  </button>
);

export const TicketForm: React.FC<TicketFormProps> = ({
  mode,
  eventStartDate,
  eventStartTime,
  eventEndDate,
  eventEndTime,
  showSubEventToggle = false,
  isExpired = false,
  initialTicket,
  onSave,
  onCancel,
  onDelete,
}) => {
  const isLocked = false;
  const soldCount = initialTicket
    ? Math.max(
        0,
        (initialTicket.ticket_number_issued ?? 0) - (initialTicket.remaining_tickets ?? 0),
      )
    : 0;
  const hasSoldTickets = soldCount > 0;

  const durationHours = getEventDurationHours(
    eventStartDate,
    eventStartTime,
    eventEndDate,
    eventEndTime,
  );
  const canToggleMultiDay = durationHours > 12;

  const [ticketType, setTicketType] = useState(initialTicket?.ticket_type ?? '');
  const [quantity, setQuantity] = useState(initialTicket?.quantity ?? 0);
  const [price, setPrice] = useState(initialTicket?.price ?? 0);
  const [description, setDescription] = useState(initialTicket?.description ?? '');
  const [salesStartDate, setSalesStartDate] = useState(initialTicket?.sales_start_date ?? '');
  const [salesStartTime, setSalesStartTime] = useState(initialTicket?.sales_start_time ?? '');
  const [salesEndDate, setSalesEndDate] = useState(initialTicket?.sales_end_date ?? '');
  const [salesEndTime, setSalesEndTime] = useState(initialTicket?.sales_end_time ?? '');
  const [hasEndPurchasePeriod, setHasEndPurchasePeriod] = useState(
    initialTicket?.has_end_purchase_period ?? false,
  );
  const [goLiveOnPublish, setGoLiveOnPublish] = useState(
    initialTicket?.go_live_on_publish ?? false,
  );
  const [subEvents, setSubEvents] = useState(initialTicket?.sub_events ?? false);
  const [multiDayAccess, setMultiDayAccess] = useState(initialTicket?.multi_day_access ?? false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const inputClass =
    'w-full bg-zinc-900/60 border border-white/8 rounded-2xl px-4 py-3.5 text-white placeholder:text-zinc-400 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-all text-[14px]';

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!ticketType.trim()) newErrors.ticketType = 'Ticket type is required';
    if (quantity < 1) newErrors.quantity = 'Quantity must be at least 1.';
    if (quantity > 10000) newErrors.quantity = 'Quantity cannot exceed 10,000.';
    if (price < 0) newErrors.price = 'Price cannot be negative';
    if (!goLiveOnPublish) {
      if (!salesStartDate) {
        newErrors.salesStartDate = 'Sales start date is required';
      } else {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        if (mode !== 'edit' && salesStartDate < todayStr) {
          newErrors.salesStartDate = 'Ticket sale start date cannot be in the past.';
        } else if (eventEndDate && salesStartDate > eventEndDate) {
          newErrors.salesStartDate = 'Ticket sale start date must be before event end date.';
        }
      }
      if (!salesStartTime) newErrors.salesStartTime = 'Sales start time is required';
    }
    if (hasEndPurchasePeriod) {
      if (!salesEndDate) {
        newErrors.salesEndDate = 'Sales end date is required';
      } else {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        if (mode !== 'edit' && salesEndDate < todayStr) {
          newErrors.salesEndDate = 'Ticket sale end date cannot be in the past.';
        } else if (salesStartDate && !goLiveOnPublish && salesEndDate < salesStartDate) {
          newErrors.salesEndDate = 'Ticket sale end date must be after ticket sale start date.';
        }
      }
      if (!salesEndTime) newErrors.salesEndTime = 'Sales end time is required';
    }
    if (multiDayAccess && !canToggleMultiDay) {
      newErrors.multiDayAccess =
        'Multi-day access is only allowed for events longer than 12 hours.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      id: initialTicket?.id,
      ticket_type: ticketType,
      price,
      quantity,
      description,
      sales_start_date: goLiveOnPublish ? undefined : salesStartDate,
      sales_start_time: goLiveOnPublish ? undefined : salesStartTime,
      sales_end_date: hasEndPurchasePeriod ? salesEndDate : undefined,
      sales_end_time: hasEndPurchasePeriod ? salesEndTime : undefined,
      has_end_purchase_period: hasEndPurchasePeriod,
      go_live_on_publish: goLiveOnPublish,
      sub_events: subEvents,
      multi_day_access: multiDayAccess,
    });
  };

  return (
    <div className="flex-1 overflow-y-auto min-h-0 space-y-5 pr-1">
      {/* Existing ticket preview (edit mode only) */}
      {mode === 'edit' && initialTicket && (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-zinc-900/60 border border-white/8">
          <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center shrink-0">
            <FiTag size={18} className="text-zinc-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium">{initialTicket.ticket_type}</p>
            <p className="text-zinc-500 text-xs">
              ${initialTicket.price} · {initialTicket.quantity} available
            </p>
          </div>
          <FiChevronDown size={16} className="text-zinc-500" />
        </div>
      )}

      {isExpired && (
        <div className="px-4 py-3 bg-amber-500/8 border border-amber-500/30 rounded-xl">
          <p className="text-amber-400 text-xs text-center">
            This ticket's sales period has ended. You can update details or extend the sales period
            to reopen sales.
          </p>
        </div>
      )}

      <fieldset disabled={isLocked} className="space-y-5 border-0 p-0 m-0 min-w-0">
        {/* Ticket Type */}
        <div>
          <FormLabel required>Ticket Type</FormLabel>
          <input
            type="text"
            value={ticketType}
            onChange={(e) => setTicketType(e.target.value)}
            placeholder="Enter Ticket Type"
            className={`${inputClass} mt-2`}
          />
          {errors.ticketType && <p className="text-red-400 text-xs mt-1">{errors.ticketType}</p>}
        </div>

        {/* Number of Tickets / Price */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel required>Number of Tickets</FormLabel>
            <input
              type="number"
              value={quantity || ''}
              onChange={(e) => setQuantity(Number(e.target.value))}
              placeholder="0"
              max={10000}
              min={1}
              className={`${inputClass} mt-2`}
            />
            {soldCount > 0 && (
              <p className="text-zinc-500 text-[11px] mt-1.5 leading-normal">
                {soldCount} ticket(s) already sold. Changing this number sets the new remaining
                tickets available for purchase.
              </p>
            )}
            {errors.quantity && <p className="text-red-400 text-xs mt-1">{errors.quantity}</p>}
          </div>
          <div>
            <FormLabel required>Price (USD)</FormLabel>
            <input
              type="number"
              value={price || ''}
              onChange={(e) => setPrice(Number(e.target.value))}
              placeholder="0"
              max={10000}
              min={0}
              className={`${inputClass} mt-2`}
            />
            {errors.price && <p className="text-red-400 text-xs mt-1">{errors.price}</p>}
          </div>
        </div>

        {/* Description */}
        <div>
          <FormLabel>Describe your event</FormLabel>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ticket description"
            rows={4}
            className={`${inputClass} mt-2 resize-none`}
          />
        </div>

        {/* Toggles */}
        <div className="space-y-3">
          <ToggleRow
            icon={<FiZap size={16} className="text-zinc-400" />}
            label="Go live when I publish"
            sublabel="Ticket sales start automatically at publish time"
            checked={goLiveOnPublish}
            onChange={setGoLiveOnPublish}
          />

          {!goLiveOnPublish && (
            <div>
              <FormLabel required>Sales Start</FormLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 px-1">
                <DateInput
                  value={salesStartDate}
                  onChange={setSalesStartDate}
                  placeholder="Start date"
                  className={`h-12 ${errors.salesStartDate ? 'border-red-500/60' : 'border-white/8'}`}
                />
                <Input
                  type="time"
                  value={salesStartTime}
                  onChange={(e) => setSalesStartTime(e.target.value)}
                  placeholder="Start time"
                  icon={FiClock}
                  className={`h-12 ${
                    errors.salesStartTime ? 'border-red-500/60' : 'border-white/8'
                  } scheme-dark`}
                />
              </div>
              {(errors.salesStartDate || errors.salesStartTime) && (
                <p className="text-red-400 text-xs mt-1">
                  {errors.salesStartDate || errors.salesStartTime}
                </p>
              )}
            </div>
          )}

          {goLiveOnPublish && (
            <div className="px-1 py-3 bg-blue-500/8 border border-blue-500/30 rounded-xl">
              <p className="text-blue-400 text-xs text-center">
                Ticket sales are scheduled to start when the event is published.
              </p>
            </div>
          )}

          <ToggleRow
            icon={<FiClock size={16} className="text-zinc-400" />}
            label="Set end purchase period"
            sublabel="Control when ticket sales stop"
            checked={hasEndPurchasePeriod}
            onChange={setHasEndPurchasePeriod}
          />

          {hasEndPurchasePeriod && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-2">
              <DateInput
                value={salesEndDate}
                onChange={setSalesEndDate}
                placeholder="End date"
                className={`h-12 ${errors.salesEndDate ? 'border-red-500/60' : 'border-white/8'}`}
              />
              <Input
                type="time"
                value={salesEndTime}
                onChange={(e) => setSalesEndTime(e.target.value)}
                placeholder="End time"
                icon={FiClock}
                className={`h-12 ${
                  errors.salesEndTime ? 'border-red-500/60' : 'border-white/8'
                } scheme-dark`}
              />
            </div>
          )}

          {showSubEventToggle && (
            <ToggleRow
              icon={<FiCheck size={16} className="text-zinc-400" />}
              label="Select Sub Events"
              sublabel="Choose which sub events this ticket applies to"
              checked={subEvents}
              onChange={setSubEvents}
            />
          )}

          {!canToggleMultiDay ? (
            <div className="relative group w-full cursor-help">
              <div className="space-y-1">
                <div className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/60 border border-white/8 opacity-60">
                  <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                    <FiTag size={16} className="text-zinc-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-zinc-500 text-sm font-medium">Enable Multi-Day Access</p>
                    <p className="text-zinc-600 text-xs">
                      {!eventStartDate || !eventEndDate || !eventStartTime || !eventEndTime
                        ? 'Please set event start and end dates/times first.'
                        : 'Multi-day access only available for events longer than 12 hours.'}
                    </p>
                  </div>
                  <div className="w-11 h-6 rounded-full p-0.5 bg-zinc-700">
                    <div className="w-5 h-5 rounded-full bg-zinc-600 translate-x-0" />
                  </div>
                </div>
                {errors.multiDayAccess && (
                  <p className="text-red-400 text-xs px-2">{errors.multiDayAccess}</p>
                )}
              </div>

              {/* Tooltip */}
              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 scale-95 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-150 origin-bottom bg-zinc-950 border border-white/10 text-zinc-300 text-xs p-3 rounded-xl shadow-xl z-50">
                <div className="text-center font-semibold mb-1 text-white">
                  Multi-Day Access Locked
                </div>
                <div className="text-zinc-400 text-[11px] leading-relaxed text-center">
                  {!eventStartDate || !eventEndDate || !eventStartTime || !eventEndTime
                    ? 'You must set the event start and end dates and times before enabling multi-day access.'
                    : `Your current event duration is ${durationHours.toFixed(1)} hours. Multi-day access requires a duration of more than 12 hours.`}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-950" />
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <ToggleRow
                icon={<FiTag size={16} className="text-zinc-400" />}
                label="Enable Multi-Day Access"
                sublabel="This ticket can be used on all days of the event with one check-in allowed per day."
                checked={multiDayAccess}
                onChange={setMultiDayAccess}
              />
              {errors.multiDayAccess && (
                <p className="text-red-400 text-xs px-2">{errors.multiDayAccess}</p>
              )}
            </div>
          )}
        </div>
      </fieldset>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        {mode === 'edit' && onDelete && (
          <Button
            type="button"
            variant="outline"
            disabled={hasSoldTickets}
            className="rounded-full px-6 border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={onDelete}
            title={
              hasSoldTickets
                ? `This ticket type cannot be deleted because ${soldCount} ticket(s) have already been sold.`
                : undefined
            }
          >
            <FiTrash2 size={15} className="mr-1.5" />
            Delete
          </Button>
        )}
        <Button type="button" onClick={onCancel} variant="ghost" className="rounded-full px-6">
          Cancel
        </Button>
        {!isLocked && (
          <Button
            type="button"
            onClick={handleSave}
            className="flex-1 rounded-full h-12 bg-white text-black hover:bg-zinc-200 font-semibold"
          >
            <FiPlus size={16} className="mr-2" />
            Save
          </Button>
        )}
      </div>
    </div>
  );
};
